import type { Route } from './+types/services.deep';
import { Link } from 'react-router';
import { Breadcrumbs, breadcrumbSchemaItems } from '~/components/breadcrumbs';
import { RelatedPages } from '~/components/related-pages';
import { buildMeta, schemaFor, breadcrumbSchema } from '~/lib/seo';
import { SITE, PRICING } from '~/data/site';
import { useBranding } from '~/lib/branding';

export const meta: Route.MetaFunction = () => [
  ...buildMeta({
    title: 'Full Deep Clean Service | Tampa Bay',
    description:
      `Complete deep clean service in Tampa Bay. Top-to-bottom reset of your home, inside cabinets, behind appliances, grout. Starts at ${PRICING.fullDeepRate.toFixed(2)}/sqft. ${PRICING.deepMinimum} minimum. Schedule today.`,
    path: '/deep',
    jsonLd: [
      schemaFor('/deep'),
      breadcrumbSchema(breadcrumbSchemaItems('/deep')),
    ].filter((s): s is Record<string, unknown> => !!s),
  }),
];

export default function DeepServicePage() {
  const branding = useBranding();
  const examplePrice = 2400 * PRICING.fullDeepRate;

  return (
    <main id="main-content">
      <Breadcrumbs path="/deep" pageLabel="Deep Clean" />
      {/* ── HEADER ───────────────────────────────────────── */}
      <section className="section">
        <div className="container-narrow">
          <p className="eyebrow page-eyebrow">One-Time Service</p>
          <h1 className="display-lg page-h1-full">
            The full deep clean,
            <em className="italic-display">when you want everything reset.</em>
          </h1>
          <p className="lede chapter-lede">
            A complete top-to-bottom reset. Inside cabinets. Behind appliances. Baseboards.
            Grout. Trim. Every space gets professional attention. For homes that need a
            comprehensive reset or haven&rsquo;t been professionally cleaned in a while.
          </p>
        </div>
      </section>

      {/* ── PRICING ───────────────────────────────────────── */}
      <section className="section-sm bg-bone-warm">
        <div className="container-narrow">
          <h2 className="display-md">How much does it cost?</h2>
          <div className="price-example price-example-mt reveal">
            <h3 className="display-sm text-bone">${PRICING.fullDeepRate.toFixed(2)} per square foot</h3>
            <div className="price-ex-line price-ex-block-mt">
              <span>Example: 2,400 sqft home<br /><small className="text-mute">Your actual price depends on your home&rsquo;s size.</small></span>
              <span className="price-ex-total price-ex-total-sm">${Math.round(examplePrice)}</span>
            </div>
            <div className="price-ex-line">
              <span>Minimum on every service<br /><small className="text-mute">Even your smallest home gets a full professional reset.</small></span>
              <span className="price-ex-total price-ex-total-sm">${PRICING.deepMinimum}</span>
            </div>
            <p className="marginalia price-example-marginalia">
              See your exact price for your home in under 60 seconds.
            </p>
          </div>
          <div className="section-cta-left">
            <Link to="/quote" className="btn btn-brass">Calculate your price →</Link>
          </div>
        </div>
      </section>

      {/* ── WHEN TO CHOOSE ────────────────────────────────── */}
      <section className="section-sm">
        <div className="container-narrow">
          <p className="eyebrow page-eyebrow">When to choose deep clean</p>
          <h2 className="display-md">For comprehensive reset.</h2>
          <div className="feature-list reveal">
            <div className="feature-item">
              <h3 className="feature-title">It&rsquo;s been a while</h3>
              <p className="feature-text">
                Your home hasn&rsquo;t been professionally cleaned in months (or ever).
                Everything needs attention. The surfaces, the crevices, everything.
              </p>
            </div>
            <div className="feature-item">
              <h3 className="feature-title">Moving in</h3>
              <p className="feature-text">
                You&rsquo;re starting fresh in this home. Full deep clean means you
                know exactly what you&rsquo;re living in. Nothing hidden.
              </p>
            </div>
            <div className="feature-item">
              <h3 className="feature-title">Seasonal mega-reset</h3>
              <p className="feature-text">
                You want your home completely reset before a major season. Inside every
                cabinet, behind furniture, baseboards, everywhere.
              </p>
            </div>
            <div className="feature-item">
              <h3 className="feature-title">Someone has allergies</h3>
              <p className="feature-text">
                A full reset removes dust, allergens, and buildup from every crevice.
                Your home becomes healthy again.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ── WHAT'S INCLUDED ───────────────────────────────── */}
      <section className="section-sm bg-bone-warm">
        <div className="container-narrow">
          <h2 className="display-md">What gets completely clean</h2>
          <div className="checklist-grid reveal">
            <div className="checklist-col">
              <h3 className="checklist-heading">Kitchen (Deep)</h3>
              <ul className="checklist">
                <li>Inside all cabinets</li>
                <li>Behind and under appliances</li>
                <li>Backsplash and grout</li>
                <li>Cabinet interiors</li>
                <li>Baseboards (walls and gap)</li>
                <li>Light fixtures</li>
                <li>Vents and fans</li>
              </ul>
            </div>
            <div className="checklist-col">
              <h3 className="checklist-heading">Bathrooms (Deep)</h3>
              <ul className="checklist">
                <li>Inside cabinets and drawers</li>
                <li>Grout lines (deep clean)</li>
                <li>Shower/tub corners</li>
                <li>Behind and under toilet</li>
                <li>Ventilation fan filters</li>
                <li>Light fixtures and mirrors</li>
                <li>Baseboards and trim</li>
              </ul>
            </div>
            <div className="checklist-col">
              <h3 className="checklist-heading">Living Spaces (Deep)</h3>
              <ul className="checklist">
                <li>Behind and under furniture</li>
                <li>All baseboards</li>
                <li>Inside closets</li>
                <li>Windows (interior and sills)</li>
                <li>Light switches and plates</li>
                <li>Door frames and handles</li>
                <li>All trim work</li>
              </ul>
            </div>
            <div className="checklist-col">
              <h3 className="checklist-heading">Bedrooms (Deep)</h3>
              <ul className="checklist">
                <li>Inside closets</li>
                <li>Under beds</li>
                <li>Windows and sills</li>
                <li>All baseboards</li>
                <li>Doors and frames</li>
                <li>Light fixtures</li>
                <li>Ceiling fan blades</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ── THE DIFFERENCE ────────────────────────────────– */}
      <section className="section-sm">
        <div className="container-narrow">
          <h2 className="display-md">Full deep clean vs. maintenance clean</h2>
          <p className="lede chapter-lede">
            <strong>Maintenance deep</strong> (${PRICING.maintenanceRate.toFixed(2)}/sqft) handles active surfaces. <strong>Full deep</strong> (${PRICING.fullDeepRate.toFixed(2)}/sqft)
            includes everything: inside cabinets, behind appliances, deep grout work, inside
            baseboards, under furniture, behind doors — every space.
          </p>
          <p className="lede chapter-lede chapter-lede-mt">
            Choose Full Deep if your home hasn&rsquo;t been professionally cleaned recently or
            needs a complete reset. Choose Maintenance if your home is well-maintained and
            just needs a professional refresh on visible surfaces.
          </p>
        </div>
      </section>

      {/* ── WHY NLC ────────────────────────────────────────── */}
      <section className="section-sm bg-bone-warm">
        <div className="container-narrow">
          <p className="eyebrow page-eyebrow">Why {branding.name}</p>
          <h2 className="display-md">Thorough professionals who <em className="italic-display">finish the job.</em></h2>
          <div className="value-props reveal">
            <div className="value-prop">
              <h3>W-2 Employees Only</h3>
              <p>Your crew is employed by us. Trained. Accountable. Benefits included.</p>
            </div>
            <div className="value-prop">
              <h3>Insured &amp; Background Checked</h3>
              <p>Every technician vetted. Insurance covers your home. You have recourse.</p>
            </div>
            <div className="value-prop">
              <h3>Per-Square-Foot Pricing</h3>
              <p>No surprises. No hidden fees or upsells. The price covers everything.</p>
            </div>
            <div className="value-prop">
              <h3>Comprehensive Training</h3>
              <p>Our crews know how to properly clean every surface, every crevice, every space.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ── CTA ────────────────────────────────────────────── */}
      <RelatedPages path="/deep" />

      <section className="cta-final">
        <div className="container-narrow cta-inner">
          <p className="cta-eyebrow">Ready for a reset?</p>
          <h2>Book your full deep clean</h2>
          <p className="cta-lede">See your price for your home. No obligation. Takes 60 seconds.</p>
          <Link to="/quote" className="btn btn-brass btn-lg">Get your price →</Link>
        </div>
      </section>
    </main>
  );
}
